import "leaflet/dist/leaflet.css";
import "./contact-map.scss";

import { useEffect, useMemo, useState } from "react";

const DEFAULT_COORDINATES = [47.0245, 28.8324];
const DEFAULT_ZOOM = 16;
const TILE_LAYER_URL =
    "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const TILE_LAYER_ATTRIBUTION = "&copy; OpenStreetMap contributors &copy; CARTO";

function normalizeCoordinates(value) {
    if (Array.isArray(value) && value.length >= 2) {
        const latitude = Number(value[0]);
        const longitude = Number(value[1]);

        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            return [latitude, longitude];
        }
    }

    if (value && typeof value === "object") {
        const latitude = Number(value.lat ?? value.latitude);
        const longitude = Number(value.lng ?? value.lon ?? value.longitude);

        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            return [latitude, longitude];
        }
    }

    return null;
}

export default function ContactMap({
    companyName,
    address,
    mapAddress,
    mapCoordinates,
    mapLink,
}) {
    const resolvedAddress = mapAddress ?? address;
    const explicitCoordinates = useMemo(
        () => normalizeCoordinates(mapCoordinates),
        [mapCoordinates],
    );
    const [mapModules, setMapModules] = useState(null);
    const [markerIcon, setMarkerIcon] = useState(null);
    const [coordinates, setCoordinates] = useState(
        explicitCoordinates ?? DEFAULT_COORDINATES,
    );
    const [mapError, setMapError] = useState(false);

    const markerEventHandlers = useMemo(
        () => ({
            click: () => {
                if (mapLink) {
                    window.open(mapLink, "_blank", "noopener,noreferrer");
                    return;
                }

                const [latitude, longitude] = coordinates;
                const params = new URLSearchParams({
                    api: "1",
                    destination: `${latitude},${longitude}`,
                    travelmode: "walking",
                });

                window.open(
                    `https://www.google.com/maps/dir/?${params.toString()}`,
                    "_blank",
                    "noopener,noreferrer",
                );
            },
        }),
        [coordinates, mapLink],
    );

    useEffect(() => {
        let isCancelled = false;

        async function loadMapModules() {
            const [{ MapContainer, Marker, TileLayer, useMap }, leaflet] =
                await Promise.all([import("react-leaflet"), import("leaflet")]);

            if (isCancelled) {
                return;
            }

            setMapModules({ MapContainer, Marker, TileLayer, useMap });
            setMarkerIcon(
                leaflet.divIcon({
                    className: "contactMap__markerWrapper",
                    html: '<span class="contactMap__marker"></span>',
                    iconSize: [24, 24],
                    iconAnchor: [12, 24],
                }),
            );
        }

        if (typeof window !== "undefined") {
            loadMapModules();
        }

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        if (explicitCoordinates) {
            setCoordinates(explicitCoordinates);
            setMapError(false);
            return;
        }

        if (!resolvedAddress) {
            setCoordinates(DEFAULT_COORDINATES);
            setMapError(false);
            return;
        }

        const controller = new AbortController();

        async function geocodeAddress() {
            try {
                const params = new URLSearchParams({
                    format: "jsonv2",
                    limit: "1",
                    q: resolvedAddress,
                });

                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
                    {
                        headers: {
                            Accept: "application/json",
                        },
                        signal: controller.signal,
                    },
                );

                if (!response.ok) {
                    throw new Error("Geocoding request failed");
                }

                const results = await response.json();
                const firstMatch = results?.[0];
                const latitude = Number(firstMatch?.lat);
                const longitude = Number(firstMatch?.lon);

                if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                    throw new Error("Invalid geocoding response");
                }

                setCoordinates([latitude, longitude]);
                setMapError(false);
            } catch (error) {
                if (error.name === "AbortError") {
                    return;
                }

                setCoordinates(DEFAULT_COORDINATES);
                setMapError(true);
            }
        }

        geocodeAddress();

        return () => {
            controller.abort();
        };
    }, [explicitCoordinates, resolvedAddress]);

    function MapCenter({ center }) {
        const map = mapModules.useMap();

        useEffect(() => {
            map.attributionControl?.setPrefix(false);
            map.setView(center, DEFAULT_ZOOM, { animate: false });
        }, [center, map]);

        return null;
    }

    return (
        <div
            className="contactMap"
            aria-label={`Harta biroului ${companyName}`}
        >
            <div className="contactMap__canvas">
                {mapModules && markerIcon ? (
                    <mapModules.MapContainer
                        center={coordinates}
                        zoom={DEFAULT_ZOOM}
                        className="contactMap__leaflet"
                        dragging
                        touchZoom
                        scrollWheelZoom
                        doubleClickZoom
                        keyboard
                        inertia
                        zoomControl={false}
                    >
                        <mapModules.TileLayer
                            attribution={TILE_LAYER_ATTRIBUTION}
                            url={TILE_LAYER_URL}
                        />
                        <mapModules.Marker
                            icon={markerIcon}
                            position={coordinates}
                            eventHandlers={markerEventHandlers}
                        />
                        <MapCenter center={coordinates} />
                    </mapModules.MapContainer>
                ) : (
                    <div className="contactMap__fallback" aria-hidden="true" />
                )}

                {mapError ? (
                    <div className="contactMap__status">
                        Nu am putut localiza adresa exact. Afisam zona
                        implicita.
                    </div>
                ) : null}
            </div>
        </div>
    );
}
